/**
 * agent.ts — API client for the deckard-agent service (PLAT-3220)
 *
 * Wraps all REST calls and the SSE streaming endpoint for the LLM chat agent.
 * Uses axios for standard REST calls and native fetch+ReadableStream for SSE
 * streaming (axios does not support streaming in the browser).
 *
 * The GATSBY_AGENT_URL env var is baked at build time by Gatsby's env pipeline.
 * In local dev this defaults to localhost:7846 (the deckard-agent service port).
 */

import { devConsole } from '@helper-functions/devConsole';
import type { PageContext } from '@hooks/agentPanelAtoms';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const AGENT_URL = process.env.GATSBY_AGENT_URL || 'http://localhost:7846';

// Log the resolved URL at module load so we can immediately see whether
// Gatsby's DefinePlugin injected the tunnel URL or fell back to localhost.
// If this shows "http://localhost:7846", the GATSBY_AGENT_URL env var was
// missing at compile time — check the docker-compose env and webpack cache.
devConsole.log(
  `[agent ${new Date().toISOString()}] AGENT_URL resolved to: ${AGENT_URL}` +
    ` (raw env: ${JSON.stringify(process.env.GATSBY_AGENT_URL)})`,
);

export const agentApi = axios.create({
  baseURL: AGENT_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

/**
 * Mirrors the Conversation row returned by the deckard-agent backend.
 * Optional fields match columns that may not be set for every conversation.
 */
export type Conversation = {
  conversationId: string;
  userId: string;
  organizationId: string;
  workspaceId?: string;
  title?: string;
  /** "active" | "archived" — values defined by the backend */
  status: string;
  model?: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * All SSE event types emitted by the /messages streaming endpoint.
 *
 * - "session"      — initial event carrying conversationId and messageId
 * - "thinking"     — Claude extended-thinking block (before final answer)
 * - "text"         — streamed text delta from the assistant
 * - "tool_use"     — assistant is calling a tool
 * - "tool_result"  — result returned from a tool call
 * - "rich_block"   — interactive UI component for user input
 * - "fallback"     — session was lost, conversation recovered from DB
 * - "done"         — stream finished successfully
 * - "error"        — terminal error from the backend
 */
export type AgentSSEEventType =
  | 'session'
  | 'thinking'
  | 'text'
  | 'tool_use'
  | 'tool_result'
  | 'rich_block'
  | 'fallback'
  | 'done'
  | 'error';

/**
 * A single parsed SSE event from the /messages stream.
 * The `data` shape varies per event type — callers should discriminate on `event`.
 */
export type AgentSSEEvent = {
  event: AgentSSEEventType;
  data: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Helper: build Bearer auth header
// ---------------------------------------------------------------------------

/** Returns an Authorization header object for the given JWT token. */
const authHeader = (token: string) => ({ Authorization: `Bearer ${token}` });

// ---------------------------------------------------------------------------
// REST functions
// ---------------------------------------------------------------------------

/**
 * Create a new conversation.
 *
 * @param token          - Keycloak JWT access token
 * @param params         - Body fields: organizationId, workspaceId?, model?, title?
 * @returns              Promise<Conversation>
 */
export const createConversation = async (
  token: string,
  params: {
    organizationId: string;
    workspaceId?: string;
    model?: string;
    title?: string;
  },
): Promise<Conversation> => {
  const response = await agentApi.post<Conversation>('/conversations', params, {
    headers: authHeader(token),
  });
  return response.data;
};

/**
 * List all conversations visible to the caller within an organization.
 *
 * @param token          - Keycloak JWT access token
 * @param organizationId - Filter conversations to this organization
 * @returns              Promise<Conversation[]>
 */
export const listConversations = async (
  token: string,
  organizationId: string,
): Promise<Conversation[]> => {
  const response = await agentApi.get<Conversation[]>('/conversations', {
    headers: authHeader(token),
    params: { organizationId },
  });
  return response.data;
};

/**
 * Fetch a single conversation by ID.
 *
 * @param token          - Keycloak JWT access token
 * @param conversationId - UUID of the conversation
 * @returns              Promise<Conversation>
 */
export const getConversation = async (
  token: string,
  conversationId: string,
): Promise<Conversation> => {
  const response = await agentApi.get<Conversation>(`/conversations/${conversationId}`, {
    headers: authHeader(token),
  });
  return response.data;
};

/**
 * Delete a conversation and all its messages.
 *
 * @param token          - Keycloak JWT access token
 * @param conversationId - UUID of the conversation to delete
 */
export const deleteConversation = async (token: string, conversationId: string): Promise<void> => {
  await agentApi.delete(`/conversations/${conversationId}`, {
    headers: authHeader(token),
  });
};

// ---------------------------------------------------------------------------
// Rich block interactions
// ---------------------------------------------------------------------------

/**
 * Submit the user's response to an interactive rich block.
 *
 * Called when the user clicks a button, selects options, or otherwise responds
 * to a rich_block SSE event.  The backend resolves the pending Promise,
 * unblocking the agent stream so it can continue.
 *
 * @param token          - Keycloak JWT access token
 * @param conversationId - UUID of the conversation
 * @param blockId        - ID of the rich block being responded to
 * @param answer         - User's selection/response payload
 */
export const respondToBlock = async (
  token: string,
  conversationId: string,
  blockId: string,
  answer: Record<string, unknown>,
): Promise<{ ok: boolean }> => {
  const response = await agentApi.post<{ ok: boolean }>(
    `/conversations/${conversationId}/respond/${blockId}`,
    answer,
    { headers: authHeader(token) },
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Live message push (PLAT-3271)
// ---------------------------------------------------------------------------

type PushMessageResponse = {
  pushed: boolean;
  messageId?: string;
  reason?: string;
};
/**
 * Push a follow-up message into a running agent stream.
 *
 * Returns `{ pushed: true, messageId }` on success.
 * Returns 409 with `{ pushed: false, reason: 'no_active_stream' }` when no
 * active stream exists — the caller should fall back to the batch queue.
 *
 * @param token          - Keycloak JWT access token
 * @param conversationId - UUID of the conversation with an active stream
 * @param prompt         - Follow-up message text
 */
export const pushMessage = async (
  token: string,
  conversationId: string,
  prompt: string,
): Promise<PushMessageResponse> => {
  const response = await agentApi.post(
    `/conversations/${conversationId}/push`,
    { prompt },
    { headers: authHeader(token) },
  );
  return response.data as PushMessageResponse;
};

// ---------------------------------------------------------------------------
// SSE streaming function
// ---------------------------------------------------------------------------

/**
 * Abort the active agent stream for a conversation.
 *
 * Sends a POST to the backend abort endpoint which signals the server-side
 * AbortController.  This is called alongside the client-side fetch abort to
 * ensure the Claude SDK stream terminates promptly even if the TCP connection
 * teardown is delayed.
 *
 * @param token          - Keycloak JWT access token
 * @param conversationId - UUID of the conversation to abort
 * @returns              Promise with { aborted: boolean }
 */
export const abortConversation = async (
  token: string,
  conversationId: string,
): Promise<{ aborted: boolean }> => {
  const response = await agentApi.post<{ aborted: boolean }>(
    `/conversations/${conversationId}/abort`,
    {},
    { headers: authHeader(token) },
  );
  return response.data;
};

/**
 * Send a user message to a conversation and stream the agent's response.
 *
 * Uses the native Fetch API + ReadableStream so the browser can consume the
 * Server-Sent Events stream incrementally without buffering the full response.
 *
 * SSE wire format (each event separated by a blank line):
 *   event: <type>\n
 *   data: <json>\n
 *   \n
 *
 * @param token          - Keycloak JWT access token
 * @param conversationId - UUID of the target conversation
 * @param prompt         - User message text
 * @param pageContext    - Optional page context (current route, params, etc.) sent as `context` in the body
 * @param onEvent        - Callback invoked for every parsed SSE event
 * @param abortSignal    - Optional AbortSignal to cancel the fetch from the client side
 * @param images         - Optional array of base64 data URIs for pasted images
 */
export const sendMessage = async (
  token: string,
  conversationId: string,
  prompt: string,
  pageContext: PageContext | undefined,
  onEvent: (event: AgentSSEEvent) => void,
  abortSignal?: AbortSignal,
  images?: string[],
): Promise<void> => {
  const url = `${AGENT_URL}/conversations/${conversationId}/messages`;

  // Build the request body — only include images if present to keep payloads small
  const body: Record<string, unknown> = { prompt, context: pageContext };
  if (images && images.length > 0) {
    body.images = images;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      // Tell the server we accept SSE so it knows to stream
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: abortSignal,
  });

  if (!response.ok) {
    throw new Error(`Agent message request failed: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Agent response has no body — cannot read SSE stream');
  }

  // Decode the byte stream as UTF-8 text
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();

  // Buffer accumulates characters until we see a complete event (blank line)
  let buffer = '';

  let result = await reader.read();
  while (!result.done) {
    buffer += result.value;

    // SSE events are delimited by a blank line ("\n\n")
    // Split on blank lines and process each complete event
    const parts = buffer.split(/\n\n/);

    // The last element may be an incomplete event — keep it in the buffer
    buffer = parts.pop() ?? '';

    parts.forEach((part) => {
      const trimmed = part.trim();
      if (!trimmed) return;

      const evt = parseSseEvent(trimmed);
      if (evt) onEvent(evt);
    });

    result = await reader.read();
  }

  // Flush any remaining buffered content as a final event
  if (buffer.trim()) {
    const evt = parseSseEvent(buffer);
    if (evt) onEvent(evt);
  }
};

// ---------------------------------------------------------------------------
// Private SSE parsing helper
// ---------------------------------------------------------------------------

/**
 * Parse a single SSE event block into an AgentSSEEvent.
 *
 * Expects lines in the form:
 *   event: <type>
 *   data: <json string>
 *
 * Returns null if the block cannot be parsed (e.g. comment lines, keep-alives).
 */
const parseSseEvent = (rawBlock: string): AgentSSEEvent | null => {
  let eventType: string | null = null;
  let dataLine: string | null = null;

  rawBlock.split('\n').forEach((line) => {
    if (line.startsWith('event:')) {
      eventType = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLine = line.slice('data:'.length).trim();
    }
  });

  if (!eventType || dataLine === null) {
    // Not a structured event (could be a comment or keep-alive ping)
    return null;
  }

  let data: Record<string, unknown> = {};
  try {
    data = JSON.parse(dataLine) as Record<string, unknown>;
  } catch {
    // Non-JSON data field — wrap raw string so callers always get an object
    data = { raw: dataLine };
  }

  return { event: eventType as AgentSSEEventType, data };
};
